"use client";

import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import ChoicePills from "../choicePills/choicePills.component";
import { useId } from "react";
import BillReceipt from "../billReceipt/billReceipt.component";
import type { DiscountCalculatorData } from "./discountCalculator.type";
import { useDiscountCalculator } from "./hooks";

interface DiscountCalculatorProps {
  data: DiscountCalculatorData;
}

const panelClass =
  "grid content-start gap-3.5 rounded-[28px] bg-white p-[26px] shadow-card";
const stepClass = "text-base leading-snug font-bold";
const fieldClass = "grid gap-2 text-[15px] font-semibold";
const numberClass =
  "h-auto rounded-[14px] border-[1.5px] border-line px-3.5 py-3 text-[22px] font-bold text-ink md:text-[22px] focus-visible:border-primary focus-visible:outline-3 focus-visible:outline-offset-1 focus-visible:outline-primary";
const eqBoxClass = "grid gap-1 rounded-[18px] px-2 py-3.5";
const eqKeyClass =
  "text-[11.5px] font-extrabold tracking-[0.06em] uppercase";
const eqValueClass = "text-[1.5rem] font-bold tracking-[-0.5px]";
const eqSignClass = "hidden text-[1.8rem] font-extrabold text-ink-muted sm:block";

// The preview's discount calculator (`[data-dc]`): the inputs on the left,
// and on the right the bill - discount = final equation, a paid/saved bar,
// a one-line summary and the resulting bill.
const DiscountCalculator = ({ data }: DiscountCalculatorProps) => {
  const calc = useDiscountCalculator(data);
  const baseId = useId();
  const billId = `${baseId}-bill`;
  const valueId = `${baseId}-value`;
  const minId = `${baseId}-min`;

  return (
    <figure role="group" aria-label={data.ariaLabel} className="m-0">
      <div className="grid items-start gap-[22px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        <div className={panelClass}>
          <h3 className={stepClass}>{data.billStep}</h3>
          <label htmlFor={billId} className={fieldClass}>
            {data.billLabel}
          </label>
          <Input
            id={billId}
            type="number"
            inputMode="numeric"
            min={0}
            step={data.billInputStep}
            value={calc.state.bill}
            onChange={(event) => calc.setBill(event.target.value)}
            className={numberClass}
          />

          <h3 className={stepClass}>{data.discountStep}</h3>
          <ChoicePills
            ariaLabel={data.typeGroupLabel}
            options={data.types}
            value={calc.state.type}
            onChange={calc.setType}
          />
          <div className={fieldClass}>
            <span>
              {data.discountLabel}
              <output htmlFor={valueId} className="ml-1.5 font-extrabold text-primary">
                {calc.valueText}
              </output>
            </span>
            <Slider
              id={valueId}
              aria-label={data.sliderAriaLabel}
              min={calc.range.min}
              max={calc.range.max}
              step={calc.range.step}
              value={[calc.state.value]}
              onValueChange={([value]) => calc.setValue(value)}
            />
          </div>

          <h3 className={stepClass}>{data.conditionStep}</h3>
          <label htmlFor={minId} className={fieldClass}>
            {data.minLabel}
          </label>
          <Input
            id={minId}
            type="number"
            inputMode="numeric"
            min={0}
            step={data.minInputStep}
            value={calc.state.min}
            onChange={(event) => calc.setMin(event.target.value)}
            className={numberClass}
          />
          <p className="text-sm leading-normal text-ink-muted">{data.fine}</p>
        </div>

        <div className={panelClass}>
          <div className="grid items-center gap-2.5 text-center sm:grid-cols-[1fr_auto_1fr_auto_1fr]">
            <div className={cn(eqBoxClass, "bg-paper")}>
              <small className={cn(eqKeyClass, "text-ink-muted")}>
                {data.equation.original}
              </small>
              <b className={eqValueClass}>{calc.billText}</b>
            </div>
            <span aria-hidden="true" className={eqSignClass}>
              −
            </span>
            <div className={cn(eqBoxClass, "bg-tone-mint text-tone-mint-ink")}>
              <small className={eqKeyClass}>{data.equation.discount}</small>
              <b className={eqValueClass}>{calc.discountText}</b>
            </div>
            <span aria-hidden="true" className={eqSignClass}>
              =
            </span>
            <div className={cn(eqBoxClass, "bg-ink text-white")}>
              <small className={cn(eqKeyClass, "text-white/80")}>
                {data.equation.final}
              </small>
              <b className={eqValueClass}>{calc.finalText}</b>
            </div>
          </div>
          <div
            aria-hidden="true"
            className="flex h-4 overflow-hidden rounded-full bg-line"
          >
            <i
              className="block h-full bg-primary transition-[width] duration-300 motion-reduce:transition-none"
              style={{ width: `${calc.payWidth}%` }}
            />
            <i
              className="block h-full bg-tone-mint-ink transition-[width] duration-300 motion-reduce:transition-none"
              style={{ width: `${calc.saveWidth}%` }}
            />
          </div>
          {/* Only the summary line is announced, so dragging the slider
              doesn't read out the whole panel on every step. */}
          <p aria-live="polite" className="text-base font-bold">
            {calc.sayText}
          </p>
          <BillReceipt
            title={data.receipt.title}
            rows={[
              { label: data.receipt.originalLabel, value: calc.billText },
              {
                label: data.receipt.discountLabel,
                value: `−${calc.discountText}`,
                tone: "minus",
              },
            ]}
            total={{ label: data.receipt.totalLabel, value: calc.finalText }}
            foot={data.receipt.foot}
          />
        </div>
      </div>
    </figure>
  );
};

export default DiscountCalculator;
